import * as React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { navigate } from 'wouter/use-location';
import { useQueryClient } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import { ICmdTableCustomerCell, ICmdTableUserCell, TExpenseTypesRecords } from './types';
import {
  EXPENSE_TYPES_CUSTOMER_ID_KEY,
  EXPENSE_TYPES_NAME_KEY,
  EXPENSE_TYPES_OWNERSHIP_KEY,
  EXPENSE_TYPES_IMPORT_CODE_KEY,
  EXPENSE_TYPES_ACTIVE_KEY,
  EXPENSE_TYPES_CREATED_AT_KEY,
  EXPENSE_TYPES_CREATED_BY_KEY,
  EXPENSE_TYPES_MODIFIED_AT_KEY,
  EXPENSE_TYPES_MODIFIED_BY_KEY,
  EXPENSE_TYPES_DETAIL_KEY,
  EXPENSE_TYPES_DELETED_AT_KEY,
  EXPENSE_TYPES_QUERY_KEY,
  EXPENSE_TYPES_TABLE_KEY,
} from './constants';
import { TABLE_OPERATING_COSTS_TYPES } from '../../../Services/TableService';
import { URL_EXPENSE_TYPES } from '../../../router/constants';
import ThSearch from '../../CmdTable/TableHeadCell/ThSearch';
import { ICmdTableColumn, ICmdTableFilter } from '../../CmdTable/types';
import CustomerCell from '../../CmdTable/TableCells/CustomerCell';
import IconCell from '../../CmdTable/TableCells/IconCell';
import DateTimeCell from '../../CmdTable/TableCells/DateTimeCell';
import ThOnlySort from '../../CmdTable/TableHeadCell/ThOnlySort';
import ThSimpleSelect from '../../CmdTable/TableHeadCell/ThSimpleSelect';
import ThMultiSelect from '../../CmdTable/TableHeadCell/ThMultiSelect';
import ThDateTime from '../../CmdTable/TableHeadCell/ThDateTime';
import TextCell from '../../CmdTable/TableCells/TextCell';
import DetailAction from '../../CmdTable/TableActions/DetailAction';
import DeleteAction from '../../CmdTable/TableActions/DeleteAction';
import { putRequest } from '../../../Services/HttpService';
import { selectedCustomersAtom } from '../../../store/recoil/customers';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
} from '../../CmdTable/CmdTableState';

interface IUseExpenseTypes {
  columns: ColumnDef<TExpenseTypesRecords>[];
  columnOrder: string[];
  columnVisibility: { [key: string]: boolean };
}

export default function useExpenseTypes(): IUseExpenseTypes {
  const { formatMessage: f } = useIntl();
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const queryClient = useQueryClient();
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(EXPENSE_TYPES_TABLE_KEY)
  );

  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(EXPENSE_TYPES_TABLE_KEY)
  );
  const columnsTable = useRecoilValue<ICmdTableColumn[]>(columnsAtom);

  const updateTable = React.useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [EXPENSE_TYPES_TABLE_KEY, filterForRequest, sortFilter, selectedCustomers],
    });
  }, [queryClient, filterForRequest, sortFilter, selectedCustomers]);

  const handleRemove = React.useCallback(
    async (id: number, customerId?: number | number[]) => {
      if (customerId && !Array.isArray(customerId)) {
        const response = await putRequest(
          `/v1/${TABLE_OPERATING_COSTS_TYPES}/${id}/remove?customerId=${customerId}`,
          { id }
        );
        if (response) {
          updateTable();
        }
      }
    },
    [updateTable]
  );

  const handleAction = (id: number, customerId?: number) => {
    const url =
      customerId && !Array.isArray(customerId)
        ? `${URL_EXPENSE_TYPES}/edit/${id}?customerId=${customerId}`
        : `${URL_EXPENSE_TYPES}/edit/${id}`;
    navigate(url);
  };

  const columns = React.useMemo<ColumnDef<TExpenseTypesRecords>[]>(
    () => [
      {
        accessorKey: EXPENSE_TYPES_NAME_KEY,
        header: () => (
          <ThSearch
            tableName={EXPENSE_TYPES_TABLE_KEY}
            id={EXPENSE_TYPES_NAME_KEY}
            title={f({ id: `table_list.columns.${EXPENSE_TYPES_QUERY_KEY}.name` })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_EXPENSE_TYPES_NAME_KEY}.tooltip` })}
            isSortable={true}
            // minWidth="176px"
            // maxWidth="300px"
          />
        ),
        cell: (info) =>
          info.row.original?.[EXPENSE_TYPES_NAME_KEY] ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 180,
      },
      {
        accessorKey: EXPENSE_TYPES_IMPORT_CODE_KEY,
        header: () => (
          <ThOnlySort
            tableName={EXPENSE_TYPES_TABLE_KEY}
            id={EXPENSE_TYPES_IMPORT_CODE_KEY}
            title={f({
              id: `table_list.columns.${EXPENSE_TYPES_QUERY_KEY}.${EXPENSE_TYPES_IMPORT_CODE_KEY}`,
            })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_EXPENSE_TYPES_ACTIVE_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.[EXPENSE_TYPES_IMPORT_CODE_KEY] ? (
            <TextCell text={info.getValue<number>()} align="right" />
          ) : (
            <TextCell text="" />
          ),
        size: 100,
      },
      {
        accessorKey: EXPENSE_TYPES_OWNERSHIP_KEY,
        header: () => (
          <ThOnlySort
            tableName={EXPENSE_TYPES_TABLE_KEY}
            id={EXPENSE_TYPES_OWNERSHIP_KEY}
            title={f({
              id: `table_list.columns.${EXPENSE_TYPES_QUERY_KEY}.${EXPENSE_TYPES_OWNERSHIP_KEY}`,
            })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_EXPENSE_TYPES_ACTIVE_KEY}.tooltip` })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.[EXPENSE_TYPES_IMPORT_CODE_KEY] ? (
            <TextCell text={info.getValue<number>()} />
          ) : (
            <TextCell text="" />
          ),
        size: 100,
      },
      {
        accessorKey: EXPENSE_TYPES_CUSTOMER_ID_KEY,
        header: () => (
          <ThSimpleSelect
            tableName={EXPENSE_TYPES_TABLE_KEY}
            id={EXPENSE_TYPES_CUSTOMER_ID_KEY}
            title={f({ id: `table_list.columns.${EXPENSE_TYPES_CUSTOMER_ID_KEY}Id` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_EXPENSE_TYPES_CUSTOMER_ID_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.[EXPENSE_TYPES_CUSTOMER_ID_KEY] ? (
            <CustomerCell customer={info.getValue<ICmdTableCustomerCell>()} />
          ) : (
            <TextCell text="" />
          ),
        size: 260,
        // minSize: 200,
      },
      {
        accessorKey: EXPENSE_TYPES_ACTIVE_KEY,
        header: () => (
          <ThSimpleSelect
            tableName={EXPENSE_TYPES_TABLE_KEY}
            id={EXPENSE_TYPES_ACTIVE_KEY}
            title={f({
              id: `table_list.columns.${EXPENSE_TYPES_QUERY_KEY}.${EXPENSE_TYPES_ACTIVE_KEY}`,
            })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_EXPENSE_TYPES_ACTIVE_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.[EXPENSE_TYPES_ACTIVE_KEY] ? (
            <IconCell isIcon={info.getValue<boolean>()} icon="cmd-icon-active text-success" />
          ) : (
            <TextCell text="" />
          ),
        size: 100,
      },

      {
        accessorKey: EXPENSE_TYPES_CREATED_BY_KEY,
        header: () => (
          <ThMultiSelect
            tableName={EXPENSE_TYPES_TABLE_KEY}
            id={EXPENSE_TYPES_CREATED_BY_KEY}
            title={f({ id: `table_list.columns.${EXPENSE_TYPES_CREATED_BY_KEY}` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_EXPENSE_TYPES_CREATED_BY_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.createdBy ? (
            <TextCell text={info.getValue<ICmdTableUserCell>().name || ''} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: EXPENSE_TYPES_CREATED_AT_KEY,
        header: () => (
          <ThDateTime
            tableName={EXPENSE_TYPES_TABLE_KEY}
            id={EXPENSE_TYPES_CREATED_AT_KEY}
            title={f({ id: `table_list.columns.${EXPENSE_TYPES_CREATED_AT_KEY}` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_EXPENSE_TYPES_CREATED_AT_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.createdAt ? (
            <DateTimeCell datetime={info.getValue<string | null>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: EXPENSE_TYPES_MODIFIED_BY_KEY,
        header: () => (
          <ThMultiSelect
            tableName={EXPENSE_TYPES_TABLE_KEY}
            id={EXPENSE_TYPES_MODIFIED_BY_KEY}
            title={f({ id: `table_list.columns.${EXPENSE_TYPES_MODIFIED_BY_KEY}` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_EXPENSE_TYPES_MODIFIED_BY_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.modifiedBy ? (
            <TextCell text={info.getValue<ICmdTableUserCell>().name || ''} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: EXPENSE_TYPES_MODIFIED_AT_KEY,
        header: () => (
          <ThDateTime
            id={EXPENSE_TYPES_MODIFIED_AT_KEY}
            tableName={EXPENSE_TYPES_TABLE_KEY}
            title={f({ id: `table_list.columns.${EXPENSE_TYPES_MODIFIED_AT_KEY}` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_EXPENSE_TYPES_MODIFIED_AT_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.modifiedBy ? (
            <DateTimeCell datetime={info.getValue<string | null>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },

      {
        accessorKey: EXPENSE_TYPES_DETAIL_KEY,
        header: () => (
          <div className="w-sm text-center th-sticky sticky-col detail">
            {f({ id: 'table.detailBtn' })}
          </div>
        ),
        cell: (info) =>
          info.row.original ? (
            <DetailAction
              id={info.row.original.id}
              tableName={TABLE_OPERATING_COSTS_TYPES}
              customerId={info.row.original.customer.id}
              onClickCallback={handleAction}
            />
          ) : null,
        size: 60, // Adjusted for consistency with header styling
        enablePinning: true,
      },
      {
        accessorKey: EXPENSE_TYPES_DELETED_AT_KEY,
        header: () => (
          <div className="w-sm text-center th-sticky sticky-col detail">
            {f({ id: 'table.deleteBtn' })}
          </div>
        ),
        cell: (info) =>
          info.row.original ? (
            <DeleteAction
              id={info.row.original.id}
              tableName={TABLE_OPERATING_COSTS_TYPES}
              customerId={info.row.original.customer.id}
              onClickCallback={handleRemove}
              showConfirmDialog={true}
            />
          ) : null,
        size: 80, // Adjusted for consistency with header styling
        enablePinning: true,
      },
    ],
    [f, handleRemove]
  );

  const columnOrder = React.useMemo(() => {
    const order = columnsTable.map((item) => item.key);
    // add the detail and delete columns to the end
    order.push(EXPENSE_TYPES_DETAIL_KEY, EXPENSE_TYPES_DELETED_AT_KEY);
    return order;
  }, [columnsTable]);

  const columnVisibility = React.useMemo(() => {
    return columnsTable.reduce<Record<string, boolean>>(
      (acc, column) => {
        acc[column.key] = !column.hidden;
        return acc;
      },
      {} as Record<string, boolean>
    );
  }, [columnsTable]);

  return {
    columns,
    columnOrder,
    columnVisibility,
  };
}
